/**
 * Button — RestOS primitive. INK = action; there is intentionally NO green CTA (§1, §6).
 * In the real monorepo this is `restos-waiter/components/ui/button.tsx` (cva-based).
 */
import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "primary" // ink CTA
  | "secondary"
  | "ghost"
  | "danger"
  | "danger-quiet";

export type ButtonSize = "sm" | "md" | "lg" | "icon";

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-foreground hover:opacity-90",
  secondary: "bg-surface text-ink border border-border hover:bg-surface-2",
  ghost: "bg-transparent text-ink hover:bg-surface-2",
  danger: "bg-danger text-danger-foreground hover:opacity-90",
  "danger-quiet": "bg-danger-soft text-danger hover:opacity-90",
};

const sizeClasses: Record<ButtonSize, string> = {
  // Touch targets ≥ 44–48px (§6).
  sm: "h-9 px-3 text-sm rounded-control gap-1.5",
  md: "h-11 px-4 text-base rounded-control gap-2",
  lg: "h-12 px-5 text-base rounded-control gap-2",
  icon: "h-11 w-11 rounded-control",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", block, className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex select-none items-center justify-center font-semibold",
        "transition-[transform,opacity] duration-150 ease-out active:scale-[0.98]",
        "focus-ring disabled:pointer-events-none disabled:opacity-50",
        variantClasses[variant],
        sizeClasses[size],
        block && "w-full",
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
